import PULSE_API from 'api/pulse/v2'
import omit from 'lodash/omit'
import { call, put, select, takeLatest } from 'redux-saga/effects'
import prepareFilterParams from 'utils/prepareFilterParams'
import NETWORK_PREFIX_MAP from 'modules/Statistics/networkPrefixMap'
import { notificationShow } from 'modules/Notifications/actions'

function* setFilteredMetrics(data) {
  const { metrics = [] } = yield select((state) => state['statistics/pulse'])

  const summary =
    Array.isArray(data.summary) && !data.summary.length ? {} : data.summary

  const disabledIdsMetrics = metrics.map((metric) => {
    const { id } = metric
    const isChildren = id.indexOf('.') > -1

    if (isChildren) {
      const [k, v] = id.split('.')
      if (!summary[k] || (summary[k] && !(v in summary[k]))) {
        return `${k}.${v}`
      }
    } else {
      if (!(id in summary)) {
        return id
      }
    }
  })

  if (!disabledIdsMetrics.length) {
    return
  }

  yield put({
    type: 'PULSE/SET_METRIC_DISABLED',
    payload: { ids: disabledIdsMetrics },
  })
}

function* fetchUserAnalyticsCampaigns({ isDemoMode }) {
  const { filters } = yield select((state) => state['statistics/pulse'])
  const params = omit(prepareFilterParams(filters), ['include_vat'])

  try {
    yield put({
      type: 'PULSE/ANALYTICS_CAMPAIGNS_FETCH_START',
      payload: 'analytics',
    })

    const { response, error } = yield call(
      isDemoMode ? PULSE_API.fetchCampaignsDemo : PULSE_API.fetchCampaigns,
      {
        network: NETWORK_PREFIX_MAP[filters.networks],
        params: params,
      },
    )

    if (error) {
      throw error
    }

    yield call(setFilteredMetrics, response.data)

    yield put({ type: 'PULSE/SET_ANALYTICS_CAMPAIGNS', payload: response })
  } catch (error) {
    yield put({
      type: 'PULSE/ANALYTICS_CAMPAIGNS_FETCH_FAIL',
      payload: 'analytics',
    })

    yield put(
      notificationShow({
        type: 'error',
        message: 'common::notifications.data_fetch_failed',
      }),
    )

    console.logXHR(error)
  }
}

function* toggleMetricActive({ payload }) {
  const { metrics } = yield select((state) => state['statistics/pulse'])
  const filteredMetricsActive = metrics.filter((metric) => metric.isActive)
  const currentMetricID = payload.id

  const currentMetricIsActive = filteredMetricsActive.find(
    (metric) => metric.id === currentMetricID,
  )

  if (filteredMetricsActive.length === 4 && !currentMetricIsActive) {
    yield put(
      notificationShow({
        type: 'warning',
        message: 'pulse::chart.warning.max',
      }),
    )
  } else if (filteredMetricsActive.length === 1 && currentMetricIsActive) {
    yield put(
      notificationShow({
        type: 'warning',
        message: 'pulse::chart.warning.min',
      }),
    )
  } else {
    yield put({
      type: 'PULSE/TOGGLE_METRIC_STATUS_ACTIVE',
      payload,
    })
  }
}

function* removeMetric({ payload }) {
  const { metrics } = yield select((state) => state['statistics/pulse'])

  const metricsShown = metrics.filter(
    (metric) => !metric.isDisabled && !metric.isHidden,
  )

  const metricsActive = metricsShown.filter(
    (metric) => !metric.isDisabled && !metric.isHidden && metric.isActive,
  )

  const metricActive = metricsShown.find(
    (metric) => metric.id === payload.id && metric.isActive,
  )

  if (
    metricsShown.length === 1 ||
    (metricsActive.length === 1 && metricActive)
  ) {
    yield put(
      notificationShow({
        type: 'warning',
        message: 'pulse::metrics.last_metric_warning',
      }),
    )
  } else {
    yield put({
      type: 'PULSE/REMOVE_METRIC',
      payload,
    })
  }
}

export default function* Sagas() {
  yield takeLatest('PULSE/FETCH_DATA', fetchUserAnalyticsCampaigns)
  yield takeLatest(
    'PULSE/GET_USER_ANALYTICS_CAMPAIGNS',
    fetchUserAnalyticsCampaigns,
  )
  yield takeLatest('PULSE/TOGGLE_METRIC_ACTIVE', toggleMetricActive)
  yield takeLatest('PULSE/REMOVE_USER_METRIC', removeMetric)
}
